import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import MGridLayout from "components/Shared/MGridLayout";
import CaseAttributesCard from "components/QualityAssurance/Sections/CaseDetails/CaseAttributesCard.tsx";
import GPSLocation from "components/QualityAssurance/Sections/CaseDetails/GPSLocation";
import PublicPropertyRecord from "components/Homeowner/CaseForms/PublicPropertyRecord";
import { getGpsData } from "requests/qualityAssurance/index.ts";
import CaseStepper from "components/Homeowner/CaseStepper";
import { STATUS_TRACKER } from "components/Homeowner/CaseStepper/StepTypes";
import DataComparison from "components/QualityAssurance/Sections/Outline/DataComparison";
import { GetDisplayFieldValue } from "components/Shared/Formatters";
import CaseHazards from "components/QualityAssurance/Sections/Outline/CaseHazards";
import { getCaseLinksRedux, getCaseLinksLimitedRedux } from "requests/homeowner/ho/index";
import PropertyCaseHistory from "components/Homeowner/CaseForms/PropertyCaseHistory";
import { CASE_PROPERTY_HISTORY_VIEW, CASE_PROPERTY_HISTORY_VIEW_LIMITED } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import usePhoenixFormsSaveComplete from "components/PhoenixFormsV4/usePhoenixFormsSaveComplete.ts";
import { getCasePhotos } from "requests/photos/index.ts";
import { MSpinner } from "components/Shared";
import ComparisonChart from "components/QualityAssurance/Sections/Outline/ComparisonChart.tsx";

const CaseDetails = (props) => {
  const mounted = useRef(true);
  const [gpsData, setGpsData] = useState({ needsFetch: true, geoCoords: { latitude: 0, longitude: 0 } });
  const [photos, setPhotos] = useState([]);

  const getPhotos = () => {
    getCasePhotos(props.qaCase.caseInfo.customerNumber, props.controlNumber, props.ssoToken, (p) => {
      setPhotos(p);
    });
  };

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!props.controlNumber) return; // how does this happen?

    if (gpsData.needsFetch) {
      fetchGpsData();
    }

    getPhotos();

    if (props.canViewPropertyHistory) {
      props.getCaseLinks(props.controlNumber);
    } else if (props.canViewCasePropertyHistoryLimited) {
      props.getCaseLinksLimited(props.controlNumber);
    }
  }, [props.controlNumber]);

  usePhoenixFormsSaveComplete(() => {
    getPhotos();
    setGpsData({
      needsFetch : true,
      ...gpsData
    });
    fetchGpsData();
  });

  const fetchGpsData = () => {
    getGpsData(props.controlNumber, (s) => {
      setGpsData(s);
    }, mounted);
  };

  if (!photos || photos.length < 1)
    return <MSpinner />;

  const showPropertyHistoryCard = props.canViewPropertyHistory || props.canViewCasePropertyHistoryLimited;

  const {
    controlNumber,
    qaCase: {
      caseInfo: {
        surveyAddress,
        showCaseAttributes,
      },
    },
    displayAddressMap,
    caseStepper,
    caseHeader,
    caseLinks,
    fetchingCaseLinks,
  } = props;

  const rows = [
    [{ component: <CaseHazards controlNumber={controlNumber} /> }],
    ...(props.qaCase?.caseInfo?.hasComparisonFields ? [[{ component: <ComparisonChart controlNumber={controlNumber} /> }]] : []),
    [{ component: <PublicPropertyRecord controlNumber={controlNumber} /> }],
    [{
      component: (
        <GPSLocation
          gpsData={gpsData}
          photos={photos}
          address={surveyAddress}
          controlNumber={controlNumber}
          refresh={fetchGpsData}
          onAddressClick={displayAddressMap}
        />
      )
    }],
    [{ component: <DataComparison controlNumber={controlNumber} propertyAddress={{ address: GetDisplayFieldValue(caseHeader.displayFields, "SurveyAddress") }} photos={photos} /> }],
    ...(showCaseAttributes ? [[{ component: <CaseAttributesCard controlNumber={controlNumber} gridHeightPixels={500} /> }]] : []),
    ...((showPropertyHistoryCard && (fetchingCaseLinks || !!caseLinks.length)) ? [[{ component: <PropertyCaseHistory caseLinks={caseLinks} fetchingCaseLinks={fetchingCaseLinks} /> }]] : []),
    [{
      component: <CaseStepper controlNumber={controlNumber} stepType={STATUS_TRACKER} />,
      className: !caseStepper.trackerActive ? "case-stepper-disabled" : ""
    }]
  ];

  return (
    <MGridLayout
      rows={rows}
    />
  );
};

CaseDetails.propTypes = {
  controlNumber: PropTypes.number.isRequired,
  qaCase: PropTypes.object.isRequired,
  photos: PropTypes.object,
  displayAddressMap: PropTypes.func,
  caseStepper: PropTypes.object,
  caseHeader: PropTypes.isRequired,
  getCaseLinks: PropTypes.func,
  getCaseLinksLimited: PropTypes.func,
  caseLinks: PropTypes.array,
  fetchingCaseLinks: PropTypes.bool,
  canViewPropertyHistory: PropTypes.bool,
  canViewCasePropertyHistoryLimited: PropTypes.bool,
  ssoToken: PropTypes.string,
};

const mapStateToProps = state => {
  return {
    qaCase: state.qualityAssurance.qaCase,
    caseStepper: state.caseStepper,
    caseHeader: state.caseForms.caseHeader,
    caseLinks: state.caseLinks.caseLinks,
    fetchingCaseLinks: state.caseLinks.fetchingCaseLinks,
    canViewPropertyHistory: userHasRight(CASE_PROPERTY_HISTORY_VIEW)(state),
    canViewCasePropertyHistoryLimited: userHasRight(CASE_PROPERTY_HISTORY_VIEW_LIMITED)(state),
    ssoToken: state.user.profile.token,
  };
};

const mapDispatchToProps = dispatch => ({
  getCaseLinks(controlNumber) {
    dispatch(getCaseLinksRedux(controlNumber));
  },
  getCaseLinksLimited(controlNumber) {
    dispatch(getCaseLinksLimitedRedux(controlNumber));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CaseDetails);
